/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903263793 — useAppStore + useTimerStore */

import { create } from 'zustand';

import { DEFAULT_LANGUAGE_KEY, IS_LOCAL_DEVELOPMENT } from '../constants';
import { Order, VendingMachine } from '../types';

const DEFAULT_ORDER: Order = {
    id: null,
    company_id: 0,
    user_id: 0,
    vm_id: 0,
    voc_id: null,
    pos_id: null,
    cell: null,
    product_id: null,
    device_type: 0,
    payment_system: 0,
    card_type: 0,
    ordered_on: '',
    extra: null,
    merchantId: null,
    src: 0,
    device_status_6: null,
    amount_used: null,
    user_rtrn_id: null,
    date_rtrn: null,
    fiscal_req: null,
    fiscal_success_ID: null,
    fiscal_failed_ID: null,
    qr: '',
    receipt_key: null,
    amount: 0,
    status: 0,
    device_status: null,
};

/**
 * The `qr` DEFAULT_VM carries until a real machine is loaded.
 *
 * A sentinel that looks like data, which is why it needs a name: it is truthy,
 * so the usual `!vm.qr` check for "no machine yet" silently passes for the
 * placeholder. Verified against production — no machine has `vm.qr = 'qr'`.
 */
const PLACEHOLDER_QR = 'qr';

export const DEFAULT_VM: VendingMachine = {
    companyId: 1,
    id: '',
    name: '',
    vmNumber: '',
    currency_code: 'UAH',
    qr: PLACEHOLDER_QR,
    type_id: '1',
    merchantIdGooglePlay: 'merchantIdGooglePlay',
    payment_provider_id: 1,
    allowed: 1,
    timeout: IS_LOCAL_DEVELOPMENT ? 999 : 1,
    ct_type: 1,
    max_credit_app: 1,
    status_connected: 1,
    language_code: DEFAULT_LANGUAGE_KEY,
    feedbacks: 0,
    fisc_receipts: 0,
    campaign: {
        message: null,
    },
    vm_id: 0,
    QRservices: 0,
    sfVersion: null,
    device_serie: null,
};

/**
 * Is this still the placeholder rather than a machine we loaded?
 *
 * Replaces `JSON.stringify(vm) === JSON.stringify(DEFAULT_VM)`, which compared
 * whole objects and was therefore key-order sensitive: any code that rebuilt the
 * VM in a different order, or added or dropped a field, silently stopped matching
 * and took the guard with it. `qr` is the field the rest of the app already trusts
 * for "is this VM real" (`useHydrateOrderFromUrl` gates on `!vmData?.qr`).
 */
export const isPlaceholderVm = (vm: VendingMachine): boolean =>
    vm.qr === PLACEHOLDER_QR;

interface AppState {
    vm: VendingMachine;
    order: Order;
    qrCode: string;
    nfc: boolean;

    setVm: (vm: VendingMachine) => void;
    setOrder: (order: Order) => void;
    updateOrder: (updater: (prev: Order) => Order) => void;
    setQrCode: (qr: string) => void;
    setNfc: (nfc: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
    vm: DEFAULT_VM,
    order: DEFAULT_ORDER,
    qrCode: '',
    nfc: false,

    setVm: (vm) => set({ vm }),
    setOrder: (order) => set({ order }),
    updateOrder: (updater) => set((state) => ({ order: updater(state.order) })),
    setQrCode: (qr) => set({ qrCode: qr }),
    setNfc: (nfc) => set({ nfc }),
}));
